import { useState, useEffect, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Users,
  Loader2,
  ArrowLeft,
  LogOut,
  Search,
  Filter,
  Trash2,
  Mail,
  Phone,
  Building2,
  Calendar,
  X,
  Save,
  Settings,
  Download,
  LayoutGrid,
  Globe,
  BarChart3,
  Table as TableIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import LeadsKanban from "../components/admin/LeadsKanban";
import LeadsInsights from "../components/admin/LeadsInsights";
import axios from "axios";
import { getAdminToken, useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${getAdminToken()}` });

const STATUS_LABELS = {
  nuevo: "Nuevo",
  contactado: "Contactado",
  reunion: "En reunión",
  ganado: "Ganado",
  perdido: "Perdido",
};

const STATUS_COLORS = {
  nuevo: "bg-blue-100 text-blue-800 border-blue-200",
  contactado: "bg-amber-100 text-amber-800 border-amber-200",
  reunion: "bg-purple-100 text-purple-800 border-purple-200",
  ganado: "bg-emerald-100 text-emerald-800 border-emerald-200",
  perdido: "bg-slate-200 text-slate-700 border-slate-300",
};

const ROL_LABELS = {
  ceo_fundador: "CEO / Fundador",
  marketing: "Marketing",
  agencia: "Agencia",
  otro: "Otro",
};

const OBJETIVO_LABELS = {
  brand_awareness: "Brand Awareness",
  generar_leads: "Generar Leads",
  contenido_rrss: "Contenido RRSS",
  comunicacion_interna: "Comunicación Interna",
  busco_proveedor: "Agencia: busca proveedor",
  ampliar_servicios: "Agencia: ampliar servicios",
  subir_fee: "Agencia: subir fee",
  otro: "Otro",
};

const ORIGEN_LABELS = {
  web: "Web (home)",
  agencias: "Landing agencias",
};

const CLIENTES_LABELS = {
  "1_2": "1-2 clientes",
  "3_5": "3-5 clientes",
  mas_5: "Más de 5 clientes",
  no_lo_se: "No lo sabe",
};

const PRESUPUESTO_LABELS = {
  menos_1500: "< 1.500€",
  "1500_3000": "1.500€ - 3.000€",
  "3000_5000": "3.000€ - 5.000€",
  mas_5000: "> 5.000€",
  no_lo_se: "No lo sé",
  soy_estudiante: "Estudiante / sin presupuesto",
};

const TIMELINE_LABELS = {
  inmediato: "Inmediato",
  "1_mes": "En 1-3 meses",
  "1_3_meses": "En 1-3 meses",
  "3_meses": "En 3-6 meses",
  "3_6_meses": "En 3-6 meses",
  explorando: "Explorando",
};

const MODALIDAD_LABELS = {
  estudio: "En estudio",
  desplazamiento: "Desplazamiento",
  indiferente: "Indiferente",
};

const BENEFICIO_LABELS = {
  referente: "Ser referente del sector",
  confianza: "Generar confianza",
  oportunidades: "Más oportunidades de venta",
  contenido: "Contenido para redes",
  networking: "Networking con invitados",
  diferenciacion: "Diferenciarse",
  talento: "Atraer talento",
  fee: "Ampliar fee de la agencia",
  no_lo_se: "No lo sabe / quiere asesoría",
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const toCSV = (leads) => {
  const headers = [
    "Fecha",
    "Nombre",
    "Empresa",
    "Web",
    "Email",
    "Teléfono",
    "Rol",
    "Objetivo",
    "Presupuesto",
    "Timeline",
    "Modalidad",
    "Origen",
    "Clientes potenciales",
    "En qué le puede ayudar",
    "Cualificado",
    "Estado",
    "Notas",
  ];
  const rows = leads.map((l) => [
    formatDate(l.created_at),
    l.nombre || "",
    l.empresa || "",
    l.web || "",
    l.email || "",
    l.telefono || "",
    ROL_LABELS[l.rol] || l.rol || "",
    OBJETIVO_LABELS[l.objetivo] || l.objetivo || "",
    PRESUPUESTO_LABELS[l.tiene_presupuesto] || l.tiene_presupuesto || "",
    TIMELINE_LABELS[l.timeline] || l.timeline || "",
    MODALIDAD_LABELS[l.modalidad] || l.modalidad || "",
    ORIGEN_LABELS[l.origen] || l.origen || "web",
    CLIENTES_LABELS[l.clientes_potenciales] || l.clientes_potenciales || "",
    (l.beneficios || []).map((b) => BENEFICIO_LABELS[b] || b).join(" | "),
    l.qualified ? "Sí" : "No",
    STATUS_LABELS[l.status] || l.status || "Nuevo",
    (l.notes || "").replace(/\n/g, " "),
  ]);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  return [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
};

export default function AdminLeadsPage() {
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    status: "all",
    rol: "all",
    presupuesto: "all",
    timeline: "all",
    modalidad: "all",
    origen: "all",
    qualified_only: false,
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [view, setView] = useState("tabla");
  const [detailNotes, setDetailNotes] = useState("");
  const [detailStatus, setDetailStatus] = useState("nuevo");
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.qualified_only) params.set("qualified_only", "true");
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.rol !== "all") params.set("rol", filters.rol);
    if (filters.presupuesto !== "all") params.set("presupuesto", filters.presupuesto);
    if (filters.timeline !== "all") params.set("timeline", filters.timeline);
    if (filters.modalidad !== "all") params.set("modalidad", filters.modalidad);
    if (filters.origen !== "all") params.set("origen", filters.origen);
    if (filters.q.trim()) params.set("q", filters.q.trim());
    return params.toString();
  }, [filters]);

  const loadLeads = useCallback(async () => {
    setIsLoading(true);
    try {
      const qs = buildQuery();
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads${qs ? `?${qs}` : ""}`, { headers: authHeaders() }),
        axios.get(`${API}/leads/stats`, { headers: authHeaders() }),
      ]);
      setLeads(leadsRes.data || []);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Error al cargar los leads");
    } finally {
      setIsLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const openDetail = (lead) => {
    setSelectedLead(lead);
    setDetailNotes(lead.notes || "");
    setDetailStatus(lead.status || "nuevo");
  };

  const closeDetail = () => {
    setSelectedLead(null);
    setDetailNotes("");
    setDetailStatus("nuevo");
  };

  const handleSaveDetail = async () => {
    if (!selectedLead) return;
    setIsSavingDetail(true);
    try {
      const { data } = await axios.patch(
        `${API}/leads/${selectedLead.id}`,
        { status: detailStatus, notes: detailNotes },
        { headers: authHeaders() }
      );
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      toast.success("Lead actualizado");
      closeDetail();
      // refresh stats
      const statsRes = await axios.get(`${API}/leads/stats`, { headers: authHeaders() });
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    } finally {
      setIsSavingDetail(false);
    }
  };

  const handleQuickStatus = async (lead, newStatus) => {
    try {
      const { data } = await axios.patch(
        `${API}/leads/${lead.id}`,
        { status: newStatus },
        { headers: authHeaders() }
      );
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
      const statsRes = await axios.get(`${API}/leads/stats`, { headers: authHeaders() });
      setStats(statsRes.data);
      toast.success(`Estado: ${STATUS_LABELS[newStatus]}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/leads/${deleteTarget.id}`, { headers: authHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Lead eliminado");
      setDeleteTarget(null);
      const statsRes = await axios.get(`${API}/leads/stats`, { headers: authHeaders() });
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCSV = () => {
    if (!leads.length) {
      toast.error("No hay leads para exportar");
      return;
    }
    const csv = toCSV(leads);
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `leads-galvani-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${leads.length} leads exportados`);
  };

  const kpi = useMemo(() => {
    if (!stats) return [];
    return [
      { label: "Total", value: stats.total, color: "from-slate-500 to-slate-700" },
      { label: "Cualificados", value: stats.qualified, color: "from-emerald-500 to-emerald-700" },
      { label: "Últimos 7 días", value: stats.last_7_days, color: "from-galvani-blue to-galvani-purple" },
      { label: "Ganados", value: stats.by_status?.ganado || 0, color: "from-galvani-pink to-orange-500" },
    ];
  }, [stats]);

  return (
    <div className="min-h-screen bg-galvani-bg" data-testid="admin-leads-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-3 group">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center shadow-md">
                <span className="font-heading font-extrabold text-lg text-white">G</span>
              </div>
              <span className="font-heading font-bold text-lg tracking-wider text-galvani-text hidden sm:inline">
                GALVANI
              </span>
            </a>
            <span className="text-slate-300 font-body text-sm">/</span>
            <span className="text-galvani-textLight font-body text-sm">CRM Leads</span>
          </div>
          <div className="flex items-center gap-4">
            {user && user !== false && (
              <span className="hidden md:inline text-xs text-galvani-textLight font-body">
                {user.email}
              </span>
            )}
            <a
              href="/admin/config"
              className="inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
              data-testid="admin-config-link"
            >
              <Settings size={16} />
              <span className="hidden sm:inline">Configuración</span>
            </a>
            <button
              onClick={() => { logout(); toast.success("Sesión cerrada"); }}
              className="inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Cerrar sesión</span>
            </button>
            <a
              href="/"
              className="hidden lg:inline-flex items-center gap-2 text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
            >
              <ArrowLeft size={16} />
              Volver
            </a>
          </div>
        </div>
      </header>

      <main className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Title */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-galvani-text">
                    <span className="gradient-text">CRM</span> Leads
                  </h1>
                  <p className="font-body text-sm text-galvani-textLight mt-1">
                    Gestiona, cualifica y da seguimiento a cada oportunidad.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex p-1 rounded-full bg-slate-100 border border-slate-200">
                  <button
                    onClick={() => setView("tabla")}
                    className={`inline-flex items-center gap-2 px-4 h-9 rounded-full font-body text-sm font-semibold transition-colors ${
                      view === "tabla" ? "bg-white text-galvani-purple shadow-sm" : "text-galvani-textLight"
                    }`}
                    data-testid="view-table-btn"
                  >
                    <TableIcon className="w-4 h-4" />
                    Tabla
                  </button>
                  <button
                    onClick={() => setView("kanban")}
                    className={`inline-flex items-center gap-2 px-4 h-9 rounded-full font-body text-sm font-semibold transition-colors ${
                      view === "kanban" ? "bg-white text-galvani-purple shadow-sm" : "text-galvani-textLight"
                    }`}
                    data-testid="view-kanban-btn"
                  >
                    <LayoutGrid className="w-4 h-4" />
                    Kanban
                  </button>
                  <button
                    onClick={() => setView("informe")}
                    className={`inline-flex items-center gap-2 px-4 h-9 rounded-full font-body text-sm font-semibold transition-colors ${
                      view === "informe" ? "bg-white text-galvani-purple shadow-sm" : "text-galvani-textLight"
                    }`}
                    data-testid="view-insights-btn"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Informe
                  </button>
                </div>
                <Button
                  onClick={handleExportCSV}
                  variant="outline"
                  className="rounded-full h-11 border-slate-300 hover:border-galvani-purple hover:text-galvani-purple"
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {kpi.map((k) => (
                <div
                  key={k.label}
                  className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm"
                  data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <p className="font-body text-xs text-galvani-textLight uppercase tracking-wider mb-2">
                    {k.label}
                  </p>
                  <p className={`font-heading font-extrabold text-3xl bg-gradient-to-r ${k.color} bg-clip-text text-transparent`}>
                    {k.value ?? 0}
                  </p>
                </div>
              ))}
            </div>

            {/* Filters */}
            {view !== "informe" && (
            <div className="mb-6 p-5 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-4 h-4 text-galvani-purple" />
                <h2 className="font-heading font-bold text-sm text-galvani-text">Filtros</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-3">
                <div className="lg:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    placeholder="Buscar nombre, email, empresa..."
                    value={filters.q}
                    onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                    className="h-11 rounded-xl pl-9"
                    data-testid="search-input"
                  />
                </div>
                <Select
                  value={filters.status}
                  onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-status">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.rol}
                  onValueChange={(v) => setFilters((f) => ({ ...f, rol: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-rol">
                    <SelectValue placeholder="Rol" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los roles</SelectItem>
                    {Object.entries(ROL_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.presupuesto}
                  onValueChange={(v) => setFilters((f) => ({ ...f, presupuesto: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-presupuesto">
                    <SelectValue placeholder="Presupuesto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(PRESUPUESTO_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.timeline}
                  onValueChange={(v) => setFilters((f) => ({ ...f, timeline: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-timeline">
                    <SelectValue placeholder="Timing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquier timing</SelectItem>
                    {Object.entries(TIMELINE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.modalidad}
                  onValueChange={(v) => setFilters((f) => ({ ...f, modalidad: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-modalidad">
                    <SelectValue placeholder="Modalidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquier modalidad</SelectItem>
                    {Object.entries(MODALIDAD_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.origen}
                  onValueChange={(v) => setFilters((f) => ({ ...f, origen: v }))}
                >
                  <SelectTrigger className="h-11 rounded-xl" data-testid="filter-origen">
                    <SelectValue placeholder="Origen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquier origen</SelectItem>
                    {Object.entries(ORIGEN_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 flex items-center gap-4">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.qualified_only}
                    onChange={(e) => setFilters((f) => ({ ...f, qualified_only: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-galvani-purple focus:ring-galvani-purple"
                    data-testid="filter-qualified-only"
                  />
                  <span className="font-body text-sm text-galvani-text">Solo cualificados</span>
                </label>
                <button
                  type="button"
                  onClick={() => setFilters({ q: "", status: "all", rol: "all", presupuesto: "all", timeline: "all", modalidad: "all", origen: "all", qualified_only: false })}
                  className="text-sm text-galvani-textLight hover:text-galvani-purple font-body transition-colors"
                  data-testid="clear-filters-btn"
                >
                  Limpiar filtros
                </button>
              </div>
            </div>
            )}

            {/* Informe view */}
            {view === "informe" && (
              <LeadsInsights
                authHeaders={authHeaders}
                beneficioLabels={BENEFICIO_LABELS}
                objetivoLabels={OBJETIVO_LABELS}
                modalidadLabels={MODALIDAD_LABELS}
                presupuestoLabels={PRESUPUESTO_LABELS}
                timelineLabels={TIMELINE_LABELS}
                clientesLabels={CLIENTES_LABELS}
                rolLabels={ROL_LABELS}
                origenLabels={ORIGEN_LABELS}
              />
            )}

            {/* Kanban view */}
            {view === "kanban" && (
              isLoading ? (
                <div className="p-16 flex items-center justify-center rounded-2xl bg-white border border-slate-200">
                  <Loader2 className="w-8 h-8 text-galvani-purple animate-spin" />
                </div>
              ) : (
                <LeadsKanban
                  leads={leads}
                  onStatusChange={handleQuickStatus}
                  onOpen={openDetail}
                  onDelete={setDeleteTarget}
                />
              )
            )}

            {/* Leads table */}
            {view === "tabla" && (
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-16 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-galvani-purple animate-spin" />
                </div>
              ) : leads.length === 0 ? (
                <div className="p-16 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="font-body text-galvani-textLight">
                    No se encontraron leads con los filtros actuales.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="leads-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider">Lead</th>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider hidden lg:table-cell">Contacto</th>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider hidden md:table-cell">Presupuesto</th>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider hidden md:table-cell">Timing</th>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider">Estado</th>
                        <th className="text-left px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider hidden xl:table-cell">Fecha</th>
                        <th className="text-right px-5 py-3 font-body font-semibold text-xs text-galvani-textLight uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((lead) => (
                        <tr
                          key={lead.id}
                          className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors"
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-5 py-4">
                            <button
                              onClick={() => openDetail(lead)}
                              className="text-left group"
                              data-testid={`lead-open-${lead.id}`}
                            >
                              <p className="font-body font-semibold text-galvani-text group-hover:text-galvani-purple transition-colors">
                                {lead.nombre}
                                {!lead.qualified && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                                    Bajo ppto
                                  </span>
                                )}
                                {lead.origen === "agencias" && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                                    Agencia
                                  </span>
                                )}
                              </p>
                              <p className="font-body text-xs text-galvani-textLight mt-0.5">
                                {lead.empresa || "Sin empresa"} · {ROL_LABELS[lead.rol] || lead.rol || "—"} · {ORIGEN_LABELS[lead.origen] || "Web (home)"}
                              </p>
                            </button>
                          </td>
                          <td className="px-5 py-4 hidden lg:table-cell">
                            <p className="font-body text-sm text-galvani-text">{lead.email}</p>
                            <p className="font-body text-xs text-galvani-textLight">{lead.telefono}</p>
                          </td>
                          <td className="px-5 py-4 hidden md:table-cell font-body text-sm text-galvani-text">
                            {PRESUPUESTO_LABELS[lead.tiene_presupuesto] || "—"}
                          </td>
                          <td className="px-5 py-4 hidden md:table-cell font-body text-sm text-galvani-text">
                            {TIMELINE_LABELS[lead.timeline] || "—"}
                          </td>
                          <td className="px-5 py-4">
                            <Select
                              value={lead.status || "nuevo"}
                              onValueChange={(v) => handleQuickStatus(lead, v)}
                            >
                              <SelectTrigger
                                className={`h-8 rounded-full border text-xs font-semibold px-3 w-auto min-w-[130px] ${STATUS_COLORS[lead.status || "nuevo"]}`}
                                data-testid={`lead-status-${lead.id}`}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                  <SelectItem key={v} value={v}>{l}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-5 py-4 hidden xl:table-cell font-body text-xs text-galvani-textLight">
                            {formatDate(lead.created_at)}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                onClick={() => setDeleteTarget(lead)}
                                className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Eliminar"
                                data-testid={`lead-delete-${lead.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-galvani-text flex items-center gap-3">
                  {selectedLead.nombre}
                  <Badge className={`text-xs font-semibold border ${STATUS_COLORS[detailStatus]}`}>
                    {STATUS_LABELS[detailStatus]}
                  </Badge>
                  {!selectedLead.qualified && (
                    <Badge className="text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                      Bajo presupuesto
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                {/* Contact */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                    <Mail className="w-4 h-4 text-galvani-purple flex-shrink-0" />
                    <span className="font-body text-sm text-galvani-text truncate">{selectedLead.email}</span>
                  </a>
                  <a href={`tel:${selectedLead.telefono}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                    <Phone className="w-4 h-4 text-galvani-purple flex-shrink-0" />
                    <span className="font-body text-sm text-galvani-text">{selectedLead.telefono}</span>
                  </a>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <Building2 className="w-4 h-4 text-galvani-purple flex-shrink-0" />
                    <span className="font-body text-sm text-galvani-text">{selectedLead.empresa || "Sin empresa"}</span>
                  </div>
                  {selectedLead.web && (
                    <a
                      href={selectedLead.web.startsWith("http") ? selectedLead.web : `https://${selectedLead.web}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid="lead-detail-web"
                    >
                      <Globe className="w-4 h-4 text-galvani-purple flex-shrink-0" />
                      <span className="font-body text-sm text-galvani-text truncate">{selectedLead.web}</span>
                    </a>
                  )}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <Calendar className="w-4 h-4 text-galvani-purple flex-shrink-0" />
                    <span className="font-body text-sm text-galvani-text">{formatDate(selectedLead.created_at)}</span>
                  </div>
                </div>

                {/* Qualification data */}
                <div className="grid grid-cols-2 gap-3 p-4 rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 border border-purple-100">
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Rol</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">{ROL_LABELS[selectedLead.rol] || selectedLead.rol || "—"}</p>
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Objetivo</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">
                      {OBJETIVO_LABELS[selectedLead.objetivo] || selectedLead.objetivo || "—"}
                      {selectedLead.objetivo_otro && (
                        <span className="block text-xs text-galvani-textLight mt-0.5">&ldquo;{selectedLead.objetivo_otro}&rdquo;</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Presupuesto</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">{PRESUPUESTO_LABELS[selectedLead.tiene_presupuesto] || "—"}</p>
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Timing</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">{TIMELINE_LABELS[selectedLead.timeline] || "—"}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Dónde quiere grabar</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">{MODALIDAD_LABELS[selectedLead.modalidad] || "—"}</p>
                  </div>
                  <div>
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Origen</p>
                    <p className="font-body text-sm font-semibold text-galvani-text">{ORIGEN_LABELS[selectedLead.origen] || "Web (home)"}</p>
                  </div>
                  {selectedLead.clientes_potenciales && (
                    <div>
                      <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight">Clientes potenciales</p>
                      <p className="font-body text-sm font-semibold text-galvani-text">{CLIENTES_LABELS[selectedLead.clientes_potenciales] || selectedLead.clientes_potenciales}</p>
                    </div>
                  )}
                </div>

                {selectedLead.beneficios?.length > 0 && (
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight mb-2">
                      En qué cree que le puede ayudar el videopodcast
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {selectedLead.beneficios.map((b) => (
                        <span
                          key={b}
                          className="text-xs font-body font-medium bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full"
                        >
                          {BENEFICIO_LABELS[b] || b}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedLead.mensaje && (
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="font-body text-[10px] uppercase tracking-wider text-galvani-textLight mb-1">Mensaje del lead</p>
                    <p className="font-body text-sm text-galvani-text whitespace-pre-wrap">{selectedLead.mensaje}</p>
                  </div>
                )}

                {/* Status */}
                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">Estado</Label>
                  <Select value={detailStatus} onValueChange={setDetailStatus}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="detail-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-galvani-text text-sm mb-2 block font-body font-medium">Notas internas</Label>
                  <Textarea
                    value={detailNotes}
                    onChange={(e) => setDetailNotes(e.target.value)}
                    placeholder="Añade notas sobre este lead, llamadas, seguimiento..."
                    className="min-h-[120px] rounded-xl"
                    data-testid="detail-notes-textarea"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <Button
                    onClick={closeDetail}
                    variant="ghost"
                    className="rounded-full"
                    data-testid="detail-cancel-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveDetail}
                    disabled={isSavingDetail}
                    className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white rounded-full px-6"
                    data-testid="detail-save-btn"
                  >
                    {isSavingDetail ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar cambios
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará el lead <strong>{deleteTarget?.nombre}</strong> permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-btn"
            >
              {isDeleting ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
